import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import {
  Container,
  useResponsive,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { GiftCard, useRedeemGiftCardDefaultValues } from '@order/features/gift-card';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { AccountScreenTitle, SignedOutView, TransparentModal } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCustomerCredit } from '../../hooks';
import { addGiftCardScreenMessages as messages } from './AddGiftCardScreen.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const AddGiftCardScreen = () => {
  const { formatMessage } = useIntl();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const params = useSafeRouteParams<{ giftCardId?: string }>();

  const { match } = useResponsive();
  const containerStyles = match([styles.containerXS, styles.containerSM]);
  const formStyles = match([styles.formContainerXS, styles.formContainerSM]);
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore

  const giftCardRedemptionEncodedData = params?.giftCardId;
  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  const isLoggedIn = useIsLoggedIn();

  const { refetchCredits } = useCustomerCredit({
    requestPolicy: 'cache-and-network',
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const redirectToParams = useMemo(
    () => ({ redirectToGiftCardId: params?.giftCardId }),
    [params?.giftCardId],
  );

  // ─────────────────────────────────────────────────────────────────────

  const screenTitle = formatMessage(messages.title);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.back();
      return;
    }

    navigation?.goBack();
  }, [isExpoRouterEnabled, router, navigation]);

  const onRedeemSuccess = useCallback(() => {
    refetchCredits();

    if (isExpoRouterEnabled) {
      router?.dismissTo('/account/gift-cards');
      return;
    }

    if (isInStoreOrdersEnabled) {
      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'GiftCards' },
      });

      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'AccountTab',
      params: { screen: 'PaymentAndGiftCards' },
    });
  }, [isExpoRouterEnabled, isInStoreOrdersEnabled, navigation, router, refetchCredits]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return (
      <SignedOutView redirectTo="add-gift-card" redirectToParams={redirectToParams} withoutFooter />
    );
  }

  return (
    <TransparentModal size="full" onClose={handleClose} disableAnimation>
      <ScrollView>
        <Container wrapperStyle={containerStyles} style={containerStyles}>
          <AccountScreenTitle title={screenTitle} />

          <View style={formStyles}>
            <GiftCard.RedemptionForm
              style={styles.withoutBorder}
              giftCardCode={defaultRedemptionValues?.giftCardCode}
              giftCardRegistrationCode={defaultRedemptionValues?.giftCardRegistrationCode} // prettier-ignore
              onRedeemSuccess={onRedeemSuccess}
            />
          </View>
        </Container>
      </ScrollView>
    </TransparentModal>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  containerSM: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    paddingVertical: theme.spacing['8'],
  },
  formContainerXS: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
  formContainerSM: {
    paddingVertical: theme.spacing['8'],
  },
  withoutBorder: {
    padding: 0,
    borderWidth: 0,
  },
});
